/**
 * useAutodetectBasemapSync — AK-377 Track 1 «перцептивный фикс детекта».
 *
 * ── Проблема (founder 2026-07-06):
 * Подложка карты — ESRI World Imagery (~1 м, чёткая). А автопоиск границ поля
 * (`POST /api/v1/fields/delineate`, способ «Автопоиск» в режиме «+ Поле»)
 * считает по Sentinel-2 TCI 10 м. Пользователь ставит точку по чёткой
 * ESRI-картинке, а обводка приходит по размытому Sentinel → визуально «не
 * совпадает» → ощущение «плохо ищет».
 *
 * ── Решение (Вариант A — перцептивное совпадение):
 * Пока активен ИМЕННО способ «Автопоиск» (method === 'detect'), показываем
 * поверх ESRI тот САМЫЙ слой Sentinel-2 TCI (`tci-snapshot-layer`), по которому
 * реально идёт детект — тогда обводка ложится на ту же картинку, что «видит»
 * алгоритм. При выходе из автопоиска (другой способ, выход из режима, unmount
 * карты) слой снова прячем (его дефолт — `visibility:none`).
 *
 * ── Почему `method === 'detect'`, а не весь режим «+ Поле» (важно после AK-373):
 * AK-373 «Пересборка режима добавления» ввёл явный сегмент-переключатель
 * способов: Автопоиск / Нарисовать / Файл. Sentinel-подложка осмысленна ТОЛЬКО
 * для «Автопоиск» — там detect по Sentinel и нужна перцептивная сцепка. При
 * «Нарисовать» (поточечное рисование, `startPolygonDraw`) и «Файл» пользователь
 * работает по чёткой ESRI-картинке — замыливать её Sentinel'ом вредно. Поэтому
 * hook принимает флаг `active`, который вызывающий формирует как
 * `addFieldActive && method === 'detect'` (см. v2.tsx). Прежняя версия (#420,
 * до AK-373) вешала overlay на весь режим — теперь это было бы регрессией UX.
 *
 * TCI-слой уже существует в setupMapLayers (source `tci-raster`,
 * `tci(selectedDate)` тайлы, тот же Sentinel-2 L2A `visual`, что берёт
 * delineation-сервис из `data.scene_rasters` index_name='TCI'). Бэкенд-работы
 * не требуется — только видимость.
 *
 * ── Оговорка (почему в панели ЕЩЁ и бейдж, Вариант B):
 * Карта рисует TCI за `selectedDate` (пикер спутника), а детект берёт «свежую
 * низкооблачную» сцену на точку тапа — даты могут не совпадать байт-в-байт.
 * Поэтому совпадение перцептивное (тот же сенсор/10 м/true-color), а не
 * гарантированно та же сцена. Бейдж «Sentinel 10 м · контур примерный» в
 * AddFieldPanel управляет ожиданием и всегда честен.
 *
 * ── Дисциплина карты:
 * - Идемпотентно (гейтим getLayer, ставим visibility только на изменение).
 * - Teardown-safe: `isMapAlive` — не трогаем стиль после map.remove()
 *   (та же грабля, что роняла getLayer на снесённой карте, PR #394).
 * - Никакого recreate source/layer, только `visibility` → без мерцания.
 */
import { useEffect } from 'react';
import type { Map as MLMap } from 'maplibre-gl';
import { LAYER_IDS } from '../setupMapLayers';
import { isMapAlive } from '../isMapAlive';

/** Слой Sentinel-2 TCI-снимка, который показываем в режиме автопоиска. */
export const AUTODETECT_TCI_LAYER_ID = LAYER_IDS.tciSnapshot;

/**
 * Идемпотентно и teardown-safe переключает видимость слоя TCI-снимка.
 * Экспортируется отдельно для юнит-теста (без React).
 */
export function applyAutodetectBasemap(map: MLMap, active: boolean): void {
  if (!isMapAlive(map)) return;
  if (!map.getLayer(AUTODETECT_TCI_LAYER_ID)) return;
  const next = active ? 'visible' : 'none';
  try {
    // Idempotent: пропускаем setLayoutProperty если visibility уже нужное —
    // без лишних перерисовок и без риска мигания.
    const current = map.getLayoutProperty(AUTODETECT_TCI_LAYER_ID, 'visibility');
    if (current === next) return;
    map.setLayoutProperty(AUTODETECT_TCI_LAYER_ID, 'visibility', next);
  } catch (e) {
    if (import.meta.env.DEV) {
      console.warn('[useAutodetectBasemapSync] setVisibility failed', e);
    }
  }
}

/**
 * Показывает Sentinel-2 TCI-подложку пока активен способ «Автопоиск».
 * На деактивацию (смена способа / выход из режима / размонтирование карты)
 * прячет обратно.
 *
 * @param map    — MapLibre instance (или null до создания).
 * @param active — способ «Автопоиск» активен (addFieldActive && method==='detect').
 */
export function useAutodetectBasemapSync(map: MLMap | null, active: boolean): void {
  useEffect(() => {
    if (!map) return;

    const apply = () => applyAutodetectBasemap(map, active);

    if (map.isStyleLoaded()) {
      apply();
    } else {
      map.once('load', apply);
    }

    return () => {
      map.off('load', apply);
      // Cleanup: гарантированно вернуть слой в скрытое состояние при
      // размонтировании эффекта (смена active / unmount карты). Гейт isMapAlive
      // внутри applyAutodetectBasemap защищает от снесённого стиля.
      applyAutodetectBasemap(map, false);
    };
  }, [map, active]);
}
